import Papa from 'papaparse';
import { FormSubmissionData, ParsedFormSubmission } from '@/types/formSubmissions';
import { normalizeText } from './textUtils';
import { z } from 'zod';

interface EntityFromDB {
  id: number;
  type: string;
  name: string;
  state: string;
  region: string;
}

interface CSVRow {
  [key: string]: string;
}

export async function parseFormSubmissionCSV(
  file: File,
  entityType: 'restaurant' | 'operator' | 'consolidator',
  existingEntities: EntityFromDB[]
): Promise<ParsedFormSubmission[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const parsed = processFormSubmissions(
            results.data as CSVRow[],
            entityType,
            existingEntities
          );
          resolve(parsed);
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(error);
      },
    });
  });
}

function processFormSubmissions(
  rows: CSVRow[],
  entityType: 'restaurant' | 'operator' | 'consolidator',
  existingEntities: EntityFromDB[]
): ParsedFormSubmission[] {
  const results: ParsedFormSubmission[] = [];

  for (const row of rows) {
    try {
      const submission = extractSubmissionData(row, entityType);
      const matched = matchWithExistingEntity(submission, existingEntities);
      results.push(matched);
    } catch (error) {
      console.error('Error processing row:', row, error);
    }
  }

  return results;
}

function extractStateFromCityState(text: string): string {
  if (!text) return '';
  
  // Extract state from formats like "Campinas - SP", "Rio de Janeiro RJ", "São Paulo/SP"
  const stateMatch = text.match(/[-/\s]([A-Z]{2})$/);
  if (stateMatch) {
    return stateMatch[1];
  }
  
  // Fallback: try to find any 2-letter uppercase at the end
  const fallbackMatch = text.match(/([A-Z]{2})$/);
  return fallbackMatch ? fallbackMatch[1] : '';
}

function inferRegionFromState(state: string): string {
  const stateRegionMap: Record<string, string> = {
    // Norte
    'AC': 'Norte', 'AP': 'Norte', 'AM': 'Norte', 'PA': 'Norte', 'RO': 'Norte', 'RR': 'Norte', 'TO': 'Norte',
    // Nordeste
    'AL': 'Nordeste', 'BA': 'Nordeste', 'CE': 'Nordeste', 'MA': 'Nordeste', 'PB': 'Nordeste', 
    'PE': 'Nordeste', 'PI': 'Nordeste', 'RN': 'Nordeste', 'SE': 'Nordeste',
    // Centro-Oeste
    'DF': 'Centro-Oeste', 'GO': 'Centro-Oeste', 'MT': 'Centro-Oeste', 'MS': 'Centro-Oeste',
    // Sudeste
    'ES': 'Sudeste', 'MG': 'Sudeste', 'RJ': 'Sudeste', 'SP': 'Sudeste',
    // Sul
    'PR': 'Sul', 'RS': 'Sul', 'SC': 'Sul',
  };
  
  return stateRegionMap[state.toUpperCase()] || '';
}

function extractSubmissionData(
  row: CSVRow,
  entityType: 'restaurant' | 'operator' | 'consolidator'
): FormSubmissionData {
  // Map CSV columns based on entity type
  let companyName = '';
  if (entityType === 'restaurant') {
    companyName = row['Nome do restaurante:'] || row['Nome do Restaurante'] || '';
  } else if (entityType === 'operator') {
    companyName = row['Nome da operadora:'] || row['Nome da Operadora'] || '';
  } else if (entityType === 'consolidator') {
    companyName = row['Nome da empresa:'] || row['Nome da Empresa'] || '';
  }

  // Extract state from "Cidade e estado:" field
  const cityState = row['Cidade e estado:'] || row['Cidade e Estado'] || '';
  const state = extractStateFromCityState(cityState);
  const region = inferRegionFromState(state);

  // Extract logo link
  const logoLink = row['Link do logo:'] || row['Link do Logo'] || row['Link da Logo'] || '';

  // Form link might not be in CSV, leave empty
  const formLink = row['Link do Formulário'] || row['Link do formulário'] || '';

  if (!companyName) {
    console.warn('Campo de nome não encontrado para entityType:', entityType, 'Colunas disponíveis:', Object.keys(row));
  }
  if (!state) {
    console.warn('Estado não encontrado em "Cidade e estado":', cityState, 'Colunas disponíveis:', Object.keys(row));
  }

  return {
    company_name: companyName.trim(),
    state: state.trim().toUpperCase(),
    region: region.trim(),
    form_link: formLink.trim(),
    logo_link: logoLink.trim(),
    entity_type: entityType,
    raw_data: row,
  };
}

function matchWithExistingEntity(
  submission: FormSubmissionData,
  existingEntities: EntityFromDB[]
): ParsedFormSubmission {
  const normalizedSubmissionName = normalizeText(submission.company_name);
  const normalizedSubmissionState = normalizeText(submission.state || '');

  // Try to find exact match by name and state
  let matchedEntity = existingEntities.find(entity => {
    const normalizedEntityName = normalizeText(entity.name);
    const normalizedEntityState = normalizeText(entity.state || '');
    
    return (
      entity.type === submission.entity_type &&
      normalizedEntityName === normalizedSubmissionName &&
      normalizedEntityState === normalizedSubmissionState
    );
  });

  // If no exact match, try fuzzy match by name only
  if (!matchedEntity) {
    matchedEntity = existingEntities.find(entity => {
      const normalizedEntityName = normalizeText(entity.name);
      return (
        entity.type === submission.entity_type &&
        normalizedEntityName.includes(normalizedSubmissionName) ||
        normalizedSubmissionName.includes(normalizedEntityName)
      );
    });
  }

  const result: ParsedFormSubmission = {
    ...submission,
    hasConflict: false,
  };

  if (matchedEntity) {
    result.matchedEntityId = matchedEntity.id;
    result.matchedEntityName = matchedEntity.name;
    
    // Check for conflicts
    if (normalizeText(matchedEntity.name) !== normalizedSubmissionName) {
      result.hasConflict = true;
      result.conflictReason = `Nome diferente: "${matchedEntity.name}" vs "${submission.company_name}"`;
    }
  } else {
    result.hasConflict = true;
    result.conflictReason = 'Entidade não encontrada no sistema';
  }

  return result;
}

// Zod schema for form submission validation with security constraints
const formSubmissionSchema = z.object({
  company_name: z.string()
    .trim()
    .min(1, 'Nome da empresa é obrigatório')
    .max(255, 'Nome da empresa deve ter no máximo 255 caracteres'),
  
  state: z.string()
    .trim()
    .regex(/^[A-Z]{2}$/, 'Estado deve ter 2 letras maiúsculas')
    .optional(),
  
  region: z.string()
    .trim()
    .max(100, 'Região deve ter no máximo 100 caracteres')
    .optional(),
  
  form_link: z.string()
    .trim()
    .url('Link do formulário inválido')
    .max(500, 'Link do formulário muito longo')
    .optional()
    .or(z.literal('')),
  
  logo_link: z.string()
    .trim()
    .url('Link da logo inválido')
    .max(500, 'Link da logo muito longo')
    .optional()
    .or(z.literal('')),
  
  entity_type: z.enum(['restaurant', 'operator', 'consolidator'], {
    errorMap: () => ({ message: 'Tipo de entidade inválido' })
  }),
  
  raw_data: z.record(z.string(), z.string())
    .refine(
      (data) => JSON.stringify(data).length <= 102400,
      'Dados brutos excedem 100KB'
    )
});

export function validateFormSubmission(submission: FormSubmissionData): string[] {
  try {
    formSubmissionSchema.parse(submission);
    return [];
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors.map(err => `${err.path.join('.')}: ${err.message}`);
    }
    return ['Erro de validação desconhecido'];
  }
}

function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
